import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useDeleteAction } from '../../hooks/useScheduledActions';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';


const DeleteActionModal = ({ isOpen, onClose, action }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const deleteAction = useDeleteAction();
    const { t } = useLanguage();

    const typeLabels = {
        create_invoice: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_INVOICE),
        create_reading: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_READING),
    };

    const frequencyLabels = {
        once: t(KEYS.SERVICE_ACTIONS_FREQ_ONCE),
        daily: t(KEYS.SERVICE_ACTIONS_FREQ_DAILY),
        weekly: t(KEYS.SERVICE_ACTIONS_FREQ_WEEKLY),
        monthly: t(KEYS.SERVICE_ACTIONS_FREQ_MONTHLY),
        yearly: t(KEYS.SERVICE_ACTIONS_FREQ_YEARLY),
    };

    const handleDelete = async () => {
        setDeleteError('');
        setIsDeleting(true);

        try {
            await deleteAction.mutateAsync({ uid: action.uid });
            onClose();
        } catch (error) {
            console.error('Error deleting action:', error);
            setDeleteError(error.message || t(KEYS.ACTION_DELETE_ERROR));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setDeleteError('');
            onClose();
        }
    };

    const typeLabel = typeLabels[action?.type] ?? action?.type ?? 'â€”';
    const frequencyLabel = frequencyLabels[action?.frequency] ?? action?.frequency ?? 'â€”';

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.ACTION_DELETE_TITLE)}
            subtitle={t(KEYS.COMMON_CANNOT_UNDO)}
            size="small"
            footer={
                <>
                    <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? t(KEYS.COMMON_DELETING) : t(KEYS.COMMON_DELETE)}
                    </Button>
                </>
            }
        >
            {deleteError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {deleteError}
                </div>
            )}

            <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-5 h-5 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-text-primary font-medium">
                        Delete <strong>{typeLabel}</strong> ({frequencyLabel})?
                    </p>
                </div>

                <div className="bg-surface-overlay rounded border border-surface-border p-4 space-y-2">
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-text-muted">{t(KEYS.ACTION_FIELD_TYPE)}</span>
                        <span className="text-sm text-text-primary">{typeLabel}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-text-muted">{t(KEYS.COMMON_FREQUENCY)}</span>
                        <span className="text-sm text-text-primary">{frequencyLabel}</span>
                    </div>
                </div>

                <p className="text-sm text-text-muted">
                    This scheduled action will be permanently removed. Any previously created resources will remain unaffected.
                </p>
            </div>
        </Modal>
    );
};

export default DeleteActionModal;
